import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BloomwellRule } from '../types/shareRule.types'
import { useToken as useMsToken } from '../util/msal'
import { paymentService, useGatedQuery } from './config'

const SHARE_RULES_QUERY_KEY = 'share-rules'

const getShareRules = async (
  token: string,
  productType?: string
): Promise<{ items: BloomwellRule[]; count: number }> => {
  if (!token) {
    return { items: [], count: 0 }
  }
  const params = productType ? `?product_type=${productType}` : ''
  const response = (await paymentService(
    `v1/internal/share-rules${params}`,
    token
  )
    .get()
    .json()) as { rules: BloomwellRule[]; total: number }
  return { items: response.rules, count: response.total }
}

export function useShareRules(productType?: string) {
  const token = useMsToken()
  return useGatedQuery({
    queryKey: [SHARE_RULES_QUERY_KEY, productType],
    queryFn: () => getShareRules(token, productType),
    enabled: !!token
  })
}

const getShareRule = async (
  ruleId: string,
  token: string
): Promise<BloomwellRule> => {
  const response = (await paymentService(
    `v1/internal/share-rules/${ruleId}`,
    token
  )
    .get()
    .json()) as { rule: BloomwellRule; history: unknown[] }
  return response.rule
}

export function useShareRule(ruleId: string) {
  const token = useMsToken()
  return useGatedQuery({
    queryKey: [SHARE_RULES_QUERY_KEY, ruleId],
    queryFn: () => getShareRule(ruleId, token),
    enabled: !!token && !!ruleId
  })
}

const createShareRule = async (
  rule: Omit<BloomwellRule, 'id' | 'created_at' | 'updated_at' | 'archived_at'>,
  token: string
): Promise<BloomwellRule> => {
  return (await paymentService('v1/internal/share-rules', token)
    .post(rule)
    .json()) as BloomwellRule
}

export function useCreateShareRule() {
  const token = useMsToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      rule: Omit<
        BloomwellRule,
        'id' | 'created_at' | 'updated_at' | 'archived_at'
      >
    ) => createShareRule(rule, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SHARE_RULES_QUERY_KEY] })
    }
  })
}

const updateShareRule = async (
  ruleId: string,
  updates: Partial<BloomwellRule>,
  token: string
): Promise<BloomwellRule> => {
  return (await paymentService(`v1/internal/share-rules/${ruleId}`, token)
    .patch(updates)
    .json()) as BloomwellRule
}

export function useUpdateShareRule() {
  const token = useMsToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ruleId,
      updates
    }: {
      ruleId: string
      updates: Partial<BloomwellRule>
    }) => updateShareRule(ruleId, updates, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SHARE_RULES_QUERY_KEY] })
    }
  })
}

const deleteShareRule = async (
  ruleId: string,
  token: string
): Promise<void> => {
  await paymentService(`v1/internal/share-rules/${ruleId}`, token)
    .delete()
    .res()
}

export function useDeleteShareRule() {
  const token = useMsToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: string) => deleteShareRule(ruleId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SHARE_RULES_QUERY_KEY] })
    }
  })
}

const getShareRuleHistory = async (
  ruleId: string,
  token: string
): Promise<BloomwellRule[]> => {
  return (await paymentService(
    `v1/internal/share-rules/${ruleId}/history`,
    token
  )
    .get()
    .json()) as BloomwellRule[]
}

export function useShareRuleHistory(ruleId: string) {
  const token = useMsToken()
  return useGatedQuery({
    queryKey: [SHARE_RULES_QUERY_KEY, ruleId, 'history'],
    queryFn: () => getShareRuleHistory(ruleId, token),
    enabled: !!token && !!ruleId
  })
}
